/**
 * Turning a camera frame into something small enough to keep.
 *
 * A phone camera frame is 3–8 MB. At 33 staff punching twice a working day that
 * is roughly 40 GB a year, against 20 GB free on the server — so resizing is
 * not a nicety, it is the reason the feature can exist at all. Downscaling to
 * 1080 px and encoding JPEG at 0.72 lands around 200–400 KB, about 6 GB a year.
 *
 * Pure functions over plain inputs, so the arithmetic can be tested without a
 * camera, a canvas or a browser.
 */

import { scaleToFit as fitInside, type Dimensions } from '@/lib/image';

/** Long edge after downscaling. Enough to recognise a person and a uniform. */
export const TARGET_LONG_EDGE = 1080;

/** Chosen against the server's 1 MB cap with room to spare for a busy frame. */
export const JPEG_QUALITY = 0.72;

export type { Dimensions } from '@/lib/image';

/**
 * Fits a frame inside the punch-photo box.
 *
 * The geometry itself lives in `@/lib/image`, shared with attachment
 * compression; this only pins the long edge a punch photo uses.
 */
export function scaleToFit(source: Dimensions, longEdge = TARGET_LONG_EDGE): Dimensions {
  return fitInside(source, longEdge);
}

export interface StampLines {
  timestamp: string;
  location: string;
  employee: string;
}

/**
 * The text burnt into the corner of the photo.
 *
 * Decoration with a purpose: it survives the image being downloaded, forwarded
 * or printed, where the database columns do not. It is *not* evidence — pixels
 * can be edited, and the row on the server is what any dispute turns on.
 *
 * The device clock writes this line, because the burn happens before the upload
 * and only the server knows the real time. That is why the server also stores
 * its own timestamp and the difference between the two.
 */
export function stampLines(
  at: Date,
  position: { latitude: number; longitude: number; accuracy: number } | null,
  employeeLabel: string,
): StampLines {
  const pad = (value: number) => String(value).padStart(2, '0');

  const timestamp =
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;

  const location = position
    ? `${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}  ±${Math.round(
        position.accuracy,
      )}m`
    : 'Location unavailable';

  return { timestamp, location, employee: employeeLabel };
}

/**
 * Draws the frame at the reduced size and burns the stamp into the corner.
 *
 * Kept separate from the encode so a caller can inspect the canvas in a test.
 */
export function drawStampedFrame(
  canvas: HTMLCanvasElement,
  source: CanvasImageSource,
  sourceSize: Dimensions,
  lines: StampLines,
): void {
  const size = scaleToFit(sourceSize);
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext('2d');
  if (!context) return;

  context.drawImage(source, 0, 0, size.width, size.height);

  // Scaled to the image so the stamp is legible on a 640 px webcam frame and
  // not absurd on a 1080 px phone one.
  const fontSize = Math.max(12, Math.round(size.width / 42));
  const padding = Math.round(fontSize * 0.7);
  const lineHeight = Math.round(fontSize * 1.35);
  const text = [lines.timestamp, lines.location, lines.employee];
  const boxHeight = lineHeight * text.length + padding * 2;

  context.fillStyle = 'rgba(0, 0, 0, 0.62)';
  context.fillRect(0, size.height - boxHeight, size.width, boxHeight);

  context.font = `${fontSize}px ui-monospace, monospace`;
  context.textBaseline = 'top';
  context.fillStyle = '#ffffff';

  text.forEach((line, index) => {
    context.fillText(
      line,
      padding,
      size.height - boxHeight + padding + index * lineHeight,
      size.width - padding * 2,
    );
  });
}

/** Encodes the canvas as JPEG. Rejects rather than resolving null. */
export function encodeJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The browser could not encode the photo. Try again.')),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}
